import { APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolOptions, getTraderSMCardHTML, getWidgetDataURI, isEmptyWidgetData, apiGet, toggleWidgetTheme, getEChartsTooltipTheme, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, searchSymbols } from "./../../helpers";
import {
  WidgetConfig,
  WidgetEvent,
  WidgetEventHandler,
  WidgetEventType,
} from "./../../types";

export class TraderversePriceRatio {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private selectedSymbols: string[] = [];

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: "closed" });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return this.getAllSymbolOptions().map((s: any) => ({
      value: s.symbol,
      label: s.name || s.symbol,
    }));
  }

  private getAllSymbolOptions(): any[] {
    const w = (window as any).getSymbolOptions;
    if (w) {
      const list = w();
      if (Array.isArray(list)) return list;
    }
    return [...getSymbolOptions("stock"), ...getSymbolOptions("crypto")];
  }

  /** Always return 2 distinct tickers for the multi-select (from options when possible). */
  private resolveSymbolPair(preferred: string[] = []): string[] {
    const options = this.fetchSymbols().map((s) => s.value);
    const fallbacks = ["MSFT", "AAPL", "GOOGL", "SPY"];
    const pool = [...options, ...fallbacks];
    const pair: string[] = [];

    for (const sym of preferred) {
      if (sym && !pair.includes(sym)) pair.push(sym);
      if (pair.length >= 2) return pair;
    }
    for (const sym of pool) {
      if (sym && !pair.includes(sym)) pair.push(sym);
      if (pair.length >= 2) return pair;
    }
    return pair.length ? pair : ["MSFT", "AAPL"];
  }

  private initializeSelectedSymbolsFromConfig(): void {
    const raw = (this.config.symbol ?? "").toString();
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 2);
    this.selectedSymbols = this.resolveSymbolPair(parts);
    this.config.symbol = this.selectedSymbols.join(",");
  }

  private buildLogoFigureHtml(ticker: string): string {
    const merged = [...getSymbolOptions("stock"), ...getSymbolOptions("crypto")];
    const symInfo =
      merged.find((s: any) => s.symbol === ticker) ||
      ({ symbol: ticker } as any);
    let logoImg = "";
    if (symInfo.type === "crypto") {
      logoImg = `<img src="https://media.traderverse.io/assets/crypto/${String(ticker).toUpperCase()}_CRYPTO.svg" alt="${ticker}" onerror="this.onerror=null;this.src='https://cdn-images.traderverse.io/crypto_dummy.svg';">`;
    } else {
      logoImg = `<img src="https://imagedelivery.net/09I_Y_JIwzvjUiGrV3dv3Q/stocks_v1/${ticker}.svg/public" alt="${ticker}">`;
    }
    return `<figure> ${logoImg} </figure>`;
  }

  private buildSelectedSymbolsLogosHtml(symbols: string[]): string {
    const chevron =
      '<svg width="11" height="6" viewBox="0 0 11 6" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.706223 0.706055L5.41424 4.47247L10.1223 0.706055" stroke="#9A95AC" stroke-width="1.41241" stroke-linecap="round" stroke-linejoin="round"/> </svg>';
    return (
      symbols.map((t) => this.buildLogoFigureHtml(t)).join("") + chevron
    );
  }

  private refreshSelectedSymbolsStrip(): void {
    const el = this.shadowRoot.getElementById("selected-symbols");
    if (el)
      el.innerHTML = this.buildSelectedSymbolsLogosHtml(this.selectedSymbols);
  }

  /** Y-axis / tooltip: up to 2 decimals; keep leading 0 (e.g. 0.75 → 0.75) */
  private formatYAxisLabel(value: number): string {
    if (value === null || value === undefined || Number.isNaN(value)) return "";
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(value);
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    return {
      headerPinned: true,
      symbol: this.resolveSymbolPair().join(","),
      theme: "default-dark",
      width: "100%",
      height: "400px",
      autosize: true,
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === "string") {
      const el =
        document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.initializeSelectedSymbolsFromConfig();
    this.createWidget();
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    if (widget) widget.classList.add("loading");
    await this.injectChartLibrary();
    this.attachStyles();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove("loading");
    this.emit("ready", {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
let dataTheme = 'default-dark';

if (this.config.theme === 'white') {
  themeClass = 'whiteMode';
  dataTheme = 'white';
} else if (this.config.theme === 'black') {
  themeClass = 'blackMode';
  dataTheme = 'black';
} else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const selectedImagesHtml = this.config.hideSymbol
      ? ""
      : this.buildSelectedSymbolsLogosHtml(this.selectedSymbols);

    const widgetHtml = `
  <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Price Ratio</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
              ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                <div class="symbolsCol" id="selected-symbols">${selectedImagesHtml}</div>
                <span id="symbol-dropdown-placeholder"></span>
                </div>`}
              <span></span>
              
            </div>

            <div class="chartDv" id="PriceCamprisonContainer">
            </div>

          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const placeholder = this.shadowRoot.getElementById(
        "symbol-dropdown-placeholder",
      );
      if (placeholder) {
        const dropdownContainerOutter = document.createElement("div");
        dropdownContainerOutter.className = "itemInner";
        const dropdownContainer = document.createElement("div");
        dropdownContainer.className = "symbol-dropdown-container";

        const input = document.createElement("input");
        input.type = "text";
        input.className = "symbol-search-input";
        input.placeholder = "Search symbol...";
        const dropdownList = document.createElement("div");
        dropdownList.className = "symbol-dropdown-list-multiple";

        let timeout = null as any;
        let requestId = 0;
        const renderDropdown = (filter = "") => {
          clearTimeout(timeout);
          timeout = setTimeout(async () => {
          const id = ++requestId;
          dropdownList.innerHTML =
            '<div style="padding:8px 12px;color:#888;">Searching...</div>';
          const keyword = filter.trim();
          let options = await searchSymbols({ keyword });
          if (id !== requestId) return;
          if (!keyword) {
            const selectedMissing = this.selectedSymbols
              .filter((s) => !options.some((item: any) => item.symbol === s))
              .map(
                (s) =>
                  this.getAllSymbolOptions().find((item: any) => item.symbol === s) || {
                    symbol: s,
                    name: s,
                    type: "stock",
                  },
              );
            options = [...selectedMissing, ...options];
          }
          const selected = options.filter((opt: any) =>
            this.selectedSymbols.includes(opt.symbol),
          );
          const unselected = options.filter(
            (opt: any) => !this.selectedSymbols.includes(opt.symbol),
          );
          const sorted = [...selected, ...unselected];
          dropdownList.innerHTML = "";
          if (sorted.length === 0) {
            dropdownList.innerHTML =
              '<div style="padding:8px 12px;color:#888;">No results</div>';
            return;
          }
          sorted.forEach((opt: any) => {
            const divOutter = document.createElement("div");
            divOutter.className = "ditem";
            const div = document.createElement("div");
            div.className = "trader-sm-card";
            if (this.selectedSymbols.includes(opt.symbol)) {
              divOutter.classList.add("selected-symbol-item");
            }
            div.innerHTML = getTraderSMCardHTML(opt);
            divOutter.innerHTML =
              '<div class="checkIcon"><i><svg width="7" height="5" viewBox="0 0 7 5" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.5 2.5L2.42929 4.42929C2.46834 4.46834 2.53166 4.46834 2.57071 4.42929L6.5 0.5" stroke="white" stroke-linecap="round"/> </svg> </i></div><div class="trader-sm-card">' +
              div.innerHTML +
              "</div>";
            divOutter.onclick = (e) => {
              e.stopPropagation();
              if (this.selectedSymbols.includes(opt.symbol)) {
                if (this.selectedSymbols.length <= 1) return;
                this.selectedSymbols = this.selectedSymbols.filter(
                  (s) => s !== opt.symbol,
                );
              } else {
                if (this.selectedSymbols.length >= 2) return;
                this.selectedSymbols.push(opt.symbol);
              }
              const firstMeta =
                this.getAllSymbolOptions().find(
                  (s: any) => s.symbol === this.selectedSymbols[0],
                ) ?? opt;
              this.onSelectChange(this.selectedSymbols, firstMeta);
              renderDropdown(input.value);
            };
            if (
              !this.selectedSymbols.includes(opt.symbol) &&
              this.selectedSymbols.length >= 2
            ) {
              divOutter.classList.add("disabled");
              divOutter.style.pointerEvents = "none";
              divOutter.style.opacity = "0.5";
            }
            dropdownList.appendChild(divOutter);
          });
          }, 300);
        };

        input.addEventListener("focus", () => {
          dropdownList.style.display = "block";
          renderDropdown(input.value);
        });
        input.addEventListener("input", () => {
          dropdownList.style.display = "block";
          renderDropdown(input.value);
        });
        input.addEventListener("blur", () => {
          setTimeout(() => {
            dropdownList.style.display = "none";
          }, 150);
        });

        dropdownContainer.appendChild(input);
        dropdownContainer.appendChild(dropdownList);
        dropdownContainerOutter.appendChild(dropdownContainer);
        placeholder.replaceWith(dropdownContainerOutter);

        renderDropdown();
      }
    }
  }

  onSelectChange(symbols: string[] | any, primaryStock?: any) {
    if (Array.isArray(symbols)) {
      this.changeSymbol(symbols.join(","), primaryStock ?? null);
    } else {
      this.selectedSymbols = [symbols.symbol].slice(0, 2);
      this.changeSymbol(symbols.symbol, symbols);
    }
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error("Failed to load CSS file:", err);
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector("script[data-chart-lib]")) {
        const script = this.shadowRoot.querySelector(
          "script[data-chart-lib]",
        ) as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement("script");
        script.src =
          "https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js";
        script.async = true;
        script.setAttribute("data-chart-lib", "true");
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol?: string, stockInfo?: any): Promise<void> {
    try {
      const raw = (symbol ?? this.config.symbol ?? "").toString();
      const tickers = raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 2);
      const pair = this.resolveSymbolPair(tickers);
      const sym1 = pair[0];
      const sym2 = pair[1];
      const opt1 = this.getAllSymbolOptions().find((s: any) => s.symbol === sym1);
      const payload = {
        symbol_1: sym1,
        symbol_2: sym2,
        days_back: 365,
        asset_type:
          opt1?.type === "crypto" ||
          stockInfo?.type === "crypto" ||
          stockInfo?.stockId?.includes?.("_CRYPTO")
            ? "crypto"
            : "stocks",
      };

      const response = await apiGet({
        method: "POST",
        url: `${APIURL}/v3/ticker/price_ratio`,
        body: payload,
      });

      if (!response.ok) throw new Error("Failed to fetch data");
      this.currentData = await response.json();

      this.updateDisplay();
      this.emit("dataUpdate", this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector("#PriceCamprisonContainer") as HTMLElement;
    if (!container) return;
    if (this.currentData == null) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (isEmptyWidgetData(this.currentData?.data)) {
      container.innerHTML = getNoDataHTML();
      return;
    }
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderAnalystRating();
  }

  private renderAnalystRating(): void {
    const container = this.shadowRoot.querySelector("#PriceCamprisonContainer");
      const chartDv = this.shadowRoot.querySelector(".chartDv") as HTMLElement;

    if (!container) return;
  const hasData =
    this.currentData?.data &&
    Object.keys(this.currentData.data).length > 0;

  if (hasData) {
    chartDv?.setAttribute("data-testing-id", "PriceRatio");
  } else {
    chartDv?.removeAttribute("data-testing-id");
  }


    const html = `
      <div class="chart" id="marketRealtimeChart"></div>
    `;
    container.innerHTML = html;
    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector(
      "#marketRealtimeChart",
    ) as HTMLElement;
    if (!container) return;
    if (!this.currentData?.data) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const data = this.currentData.data;

    const stockNames = Object.keys(data); // e.g., ['MSFT', 'SPY']
    const timestamps = Object.keys(data[stockNames[0]]).map((ts) => Number(ts));
    const formattedDates = timestamps.map((ts) => {
      const date = new Date(ts);
      return date.toISOString().split("T")[0]; // format: YYYY-MM-DD
    });

    const seriesData = stockNames.map((symbol, i) => {
      const prices = Object.values(data[symbol]);
      const colorList = ["#2196F3", "#4CAF50", "#FFC107", "#FF5722"];
      return {
        name: symbol,
        type: "line",
        smooth: true,
        data: prices,
        lineStyle: { color: colorList[i % colorList.length], width: 2 },
        itemStyle: { color: colorList[i % colorList.length] },
        showSymbol: false,
      };
    });

    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    // const isBlack = this.config.theme === 'black';
    const widgetBox = this.shadowRoot.querySelector(
      ".widgetBox",
    ) as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue(
      "--charts-text-color",
    );
    const axisLineColor = getComputedStyle(widgetBox).getPropertyValue(
      "--charts-axis-color",
    );
    const splitLineColor = getComputedStyle(widgetBox).getPropertyValue(
      "--charts-split-line-color",
    );
    const fontSize =
      getComputedStyle(widgetBox).getPropertyValue("--font-size-sm");

    const option = {
      backgroundColor: "transparent",
      legend: {
        show: false, // UI hidden rahegi, logic kaam karega
        selected: stockNames.reduce((acc: any, s: string) => {
          acc[s] = true; // default all ON
          return acc;
        }, {}),
      },
      tooltip: {
        trigger: "axis",
        ...getEChartsTooltipTheme(widgetBox),
        valueFormatter: (val: number | string | null) => {
          if (val === null || val === undefined || val === "") return "";
          const n = Number(val);
          if (Number.isNaN(n)) return String(val);
          return this.formatYAxisLabel(n);
        },
      },
      grid: { left: 0, right: 0, top: 10, bottom: 8, containLabel: true },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: formattedDates,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: {
          color: textColor,
          fontSize: fontSize,
          margin: 12,
          alignMinLabel: "left",
          alignMaxLabel: "right",
        },
      },
      yAxis: {
        type: "value",
        min: "dataMin",
        axisLine: { lineStyle: { color: axisLineColor } },
        splitLine: { lineStyle: { color: splitLineColor } },
        axisLabel: {
          color: textColor,
          fontSize: fontSize,
          formatter: (val: number | string) =>
            this.formatYAxisLabel(Number(val)),
        },
      },
      series: seriesData,
    };

    this.currentChart = echarts.init(container);
    this.currentChart.setOption(option);
      this.renderCustomLegends(stockNames, seriesData);


  }

 private renderCustomLegends(symbols: string[], series: any[]): void {
  const legendsContainer = this.shadowRoot.querySelector(
    "#custom-legends",
  ) as HTMLElement;

  if (!legendsContainer) return;

  legendsContainer.innerHTML = "";

  series.forEach((s) => {
    const el = document.createElement("span");
    el.className = "toggle-series active";
    el.setAttribute("data-symbol", s.name);
    el.style.backgroundColor = s.lineStyle.color;
    el.title = s.name;

    el.addEventListener("click", () => {
      const isActive = el.classList.contains("active");

      this.currentChart.dispatchAction({
        type: isActive ? "legendUnSelect" : "legendSelect",
        name: s.name,
      });

      el.classList.toggle("active");
    });

    legendsContainer.appendChild(el);
  });
}



  private changeSymbol(stockId: string, stock: any = null): void {
    const tickers = String(stockId)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 2);
    if (tickers.length === 0) return;
    this.selectedSymbols = tickers;
    const joined = this.selectedSymbols.join(",");
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      joined,
      (id: any, stockInfo: any) => this.setupChart(id, stockInfo),
      (type: any, data: any) => this.emit(type, data),
    );
    this.refreshSelectedSymbolsStrip();
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list)
      this.eventHandlers.set(
        type,
        list.filter((h) => h !== handler),
      );
  }
  public setSymbol(symbol: string): void {
    const tickers = this.resolveSymbolPair(
      symbol
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 2),
    );
    const first = this.getAllSymbolOptions().find((s: any) => s.symbol === tickers[0]);
    this.changeSymbol(tickers.join(","), first ?? null);
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    return await getWidgetDataURI(widget);
  }
public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
  if (!this.shadowRoot) return;

  this.config.theme = toggleWidgetTheme(
    this.shadowRoot,
    theme
  ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

  this.updateDisplay();
}
  public destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = "";
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((h) => h(evt));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window {
    TraderversePriceRatio: typeof TraderversePriceRatio;
  }
}
if (typeof window !== "undefined") {
  window.TraderversePriceRatio = TraderversePriceRatio;
}
